import {
  type ParticipantRestrictivePractice,
  RestrictivePracticeStatus,
} from '@piccolohealth/pbs-common';
import { ChakraV3, Spin } from '@piccolohealth/ui';
import { matchSorter, P } from '@piccolohealth/util';
import React from 'react';
import { showModal } from '../../components/generic/Modal';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { useParticipantRestrictivePracticesQuery } from '../../graphql/hooks/useParticipantQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { ParticipantRestrictivePracticeCard } from './components/ParticipantRestrictivePracticeCard';
import { ParticipantRestrictivePracticeCreateButton } from './components/ParticipantRestrictivePracticeCreateButton';
import { ParticipantRestrictivePracticeCreateModal } from './components/ParticipantRestrictivePracticeCreateModal';
import { ParticipantRestrictivePracticeFilterControls } from './components/ParticipantRestrictivePracticeFilterControls';
import { useParticipantRestrictivePracticesFilter } from './hooks/useParticipantRestrictivePracticesFilter';

interface Props {
  participantId: string;
  isDisabled?: boolean;
}

export const ParticipantRestrictivePractices = (props: Props) => {
  const { participantId, isDisabled } = props;

  const { organization } = useAppContext();

  const filter = useParticipantRestrictivePracticesFilter();

  const { data, isLoading, error } = useParticipantRestrictivePracticesQuery({
    organizationId: organization.id,
    participantId,
  });

  const restrictivePractices = React.useMemo(() => {
    const results =
      (data?.organization?.participant?.restrictivePractices as ParticipantRestrictivePractice[]) ??
      [];

    const descriptionFiltered = matchSorter(results, filter.descriptionFilter, {
      keys: ['description'],
    });

    const typeFiltered = !P.isEmpty(filter.typeFilter)
      ? descriptionFiltered.filter((v) => filter.typeFilter.includes(v.type))
      : descriptionFiltered;

    const statusFiltered = !P.isEmpty(filter.statusFilter)
      ? typeFiltered.filter((v) => filter.statusFilter.includes(v.status))
      : typeFiltered;

    // I want to sort by the status of the restrictive practice. Ongoing always in front of ceased
    const sorted = statusFiltered.sort((a, b) => {
      if (
        a.status === RestrictivePracticeStatus.Ongoing &&
        b.status === RestrictivePracticeStatus.Ceased
      ) {
        return -1;
      }
      if (
        a.status === RestrictivePracticeStatus.Ceased &&
        b.status === RestrictivePracticeStatus.Ongoing
      ) {
        return 1;
      }
      return 0;
    });

    return sorted;
  }, [
    data?.organization?.participant?.restrictivePractices,
    filter.descriptionFilter,
    filter.statusFilter,
    filter.typeFilter,
  ]);

  const content = P.run(() => {
    if (isLoading) {
      return <Spin />;
    }

    if (error) {
      return <PiccoloError error={error} />;
    }

    return (
      <ChakraV3.Wrap gap={4}>
        {restrictivePractices.map((restrictivePractice) => (
          <ChakraV3.WrapItem key={restrictivePractice.id} w='full' maxW='lg' minH='xs'>
            <ParticipantRestrictivePracticeCard
              isDisabled={isDisabled}
              participantId={participantId}
              participantRestrictivePractice={restrictivePractice}
            />
          </ChakraV3.WrapItem>
        ))}
        <ChakraV3.WrapItem w='full' maxW='lg' minH='xs'>
          <ParticipantRestrictivePracticeCreateButton
            onClick={() => {
              showModal(ParticipantRestrictivePracticeCreateModal, {
                participantId,
              });
            }}
            disabled={isDisabled}
          />
        </ChakraV3.WrapItem>
      </ChakraV3.Wrap>
    );
  });

  return (
    <ChakraV3.Stack px={6} py={4} gap={4}>
      <ParticipantRestrictivePracticeFilterControls filter={filter} />
      {content}
    </ChakraV3.Stack>
  );
};
